import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  adminApi,
  CallDetail,
  CallSummary,
  EmailLogEntry,
  PaymentRecord,
  TranscriptMessage,
  UserDetail,
  UserFeedbackEntry,
} from './adminApi';
import { AnswerBadge, EmailStatusBadge } from './AdminEmails';
import { SubscriptionEventRow } from './AdminSubscriptions';
import { fmtAdminDateTime } from './timeFormat';
import './Admin.css';

/** What picked up, when the bot heard a tone: an answering machine or a fax. */
export function AnsweredByBadge({ answeredBy }: { answeredBy?: string | null }) {
  if (!answeredBy) return null;
  const label =
    answeredBy === 'machine' ? 'answering machine' : answeredBy === 'fax' ? 'fax machine' : answeredBy;
  return <span className={`admin-badge answered-by ${answeredBy}`}>{label}</span>;
}

/** A call's transcript, one message per line (also used by the P&L tab). */
/** Who said a transcript line, in the words the rest of the app uses. */
const SPEAKER: Record<string, string> = { user: 'Callee', assistant: 'Callout agent', event: 'event' };

export function TranscriptMessages({ messages }: { messages: TranscriptMessage[] }) {
  return (
    <div className="admin-transcript">
      {messages.length === 0 && <div className="admin-muted">Empty transcript</div>}
      {messages.map((m, i) => (
        <div className={`admin-msg ${m.role}`} key={i}>
          <span className="who">{SPEAKER[m.role] ?? m.role}</span>
          {m.content}
        </div>
      ))}
    </div>
  );
}

function CallCard({ call }: { call: CallSummary }) {
  const [detail, setDetail] = useState<CallDetail | null>(null);
  const [transcript, setTranscript] = useState<TranscriptMessage[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadDetail = async () => {
    if (detail) return;
    setLoading(true);
    setError('');
    try {
      setDetail(await adminApi.callDetail(call.callId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load call');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next) await loadDetail();
  };

  const toggleTranscript = async () => {
    const next = !showTranscript;
    setShowTranscript(next);
    if (next && !transcript) {
      try {
        const res = await adminApi.transcript(call.callId);
        setTranscript(res.transcript);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load transcript');
      }
    }
  };

  const statusClass = `admin-badge ${call.status || ''}`;

  return (
    <div className="admin-call">
      <div className="admin-call-head">
        <div>
          <strong>{call.toNumber}</strong>{' '}
          <span className={statusClass}>{call.status || 'unknown'}</span>{' '}
          <AnsweredByBadge answeredBy={call.answeredBy} />
          {call.deletionStatus && (
            <span className="admin-badge deleted">
              {call.deletionStatus === 'pending' ? 'deleted · in review' : 'deleted · erased'}
            </span>
          )}
        </div>
        <div className="admin-call-meta">
          <span>{fmtAdminDateTime(call.initiatedAt)}</span>
          <span>{call.duration != null ? `${call.duration}s` : '—'}</span>
          <span>{call.source || '—'}</span>
          {call.workflow && <span>{call.workflow}</span>}
        </div>
      </div>

      <div className="admin-call-meta" style={{ marginTop: 8 }}>
        {call.hasPrompt && <span>· prompt</span>}
        {call.hasAudio && <span>· audio</span>}
        {call.hasTranscript && <span>· transcript</span>}
        <span>· credit {call.creditDeducted ? 'deducted' : 'not deducted'}</span>
      </div>

      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="admin-inline-btn" onClick={toggleExpand}>
          {expanded ? 'Hide details' : 'Details'}
        </button>
        {call.hasTranscript && (
          <button className="admin-inline-btn" onClick={toggleTranscript}>
            {showTranscript ? 'Hide transcript' : 'Transcript'}
          </button>
        )}
        <Link className="admin-inline-btn" to={`/admin/calls/${call.callId}`}>
          Call page
        </Link>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {loading && <div className="admin-muted" style={{ marginTop: 8 }}>Loading…</div>}

      {expanded && detail && (
        <>
          {detail.prompt && (
            <div className="admin-prompt">
              <strong>Prompt</strong>
              {'\n'}
              {detail.prompt}
            </div>
          )}
          {(detail.objective || detail.context) && (
            <div className="admin-prompt">
              {detail.objective && (
                <>
                  <strong>Objective</strong>
                  {'\n'}
                  {detail.objective}
                  {'\n\n'}
                </>
              )}
              {detail.context && (
                <>
                  <strong>Context</strong>
                  {'\n'}
                  {JSON.stringify(detail.context, null, 2)}
                </>
              )}
            </div>
          )}
          {detail.audioUrl && (
            <audio className="admin-audio" controls src={detail.audioUrl} preload="none" />
          )}
          <div className="admin-links" style={{ marginTop: 10 }}>
            {detail.audioUrl && (
              <a href={detail.audioUrl} target="_blank" rel="noopener noreferrer">
                Download audio
              </a>
            )}
            {detail.transcriptUrl && (
              <a href={detail.transcriptUrl} target="_blank" rel="noopener noreferrer">
                Raw transcript JSON
              </a>
            )}
          </div>
          {(detail.attribution || detail.landingPage) && (
            <div className="admin-call-meta" style={{ marginTop: 10 }}>
              {detail.landingPage && <span>landing: {detail.landingPage}</span>}
              {detail.attribution?.utmSource && (
                <span>utm_source: {detail.attribution.utmSource}</span>
              )}
              {detail.attribution?.utmCampaign && (
                <span>utm_campaign: {detail.attribution.utmCampaign}</span>
              )}
            </div>
          )}
        </>
      )}

      {showTranscript && transcript && <TranscriptMessages messages={transcript} />}
    </div>
  );
}

function PaymentRow({ p }: { p: PaymentRecord }) {
  return (
    <div className="admin-call" style={{ padding: '10px 14px' }}>
      <div className="admin-call-head">
        <strong>{p.planName || p.eventType || 'payment'}</strong>
        <div className="admin-call-meta">
          <span>{fmtAdminDateTime(p.createdAt)}</span>
          {p.creditsGranted != null && <span>+{p.creditsGranted} credits</span>}
          {p.billingInterval && <span>{p.billingInterval}</span>}
          {p.status && <span>{p.status}</span>}
        </div>
      </div>
    </div>
  );
}

function EmailRow({ e }: { e: EmailLogEntry }) {
  return (
    <div className="admin-call" style={{ padding: '10px 14px' }}>
      <div className="admin-call-head">
        <div>
          <strong>{e.subject || e.campaign}</strong> <EmailStatusBadge status={e.status} />
        </div>
        <div className="admin-call-meta">
          <span>{fmtAdminDateTime(e.sentAt || e.createdAt)}</span>
          <span>{e.campaign}</span>
          {e.lang && <span>{e.lang}</span>}
        </div>
      </div>
      {e.error && <div className="admin-muted" style={{ marginTop: 6 }}>{e.error}</div>}
      {e.text && (
        <details style={{ marginTop: 6 }}>
          <summary className="admin-muted">Show email</summary>
          <pre className="admin-prompt journey-long">{e.text}</pre>
        </details>
      )}
    </div>
  );
}

function FeedbackRow({ f }: { f: UserFeedbackEntry }) {
  const picks = [...(f.useCases || []), ...(f.issues || [])];
  return (
    <div className="admin-call" style={{ padding: '10px 14px' }}>
      <div className="admin-call-head">
        <div>
          <AnswerBadge value={f.metNeeds} /> <strong>{f.campaign}</strong>
        </div>
        <div className="admin-call-meta">
          <span>{fmtAdminDateTime(f.updatedAt)}</span>
          {f.survey && <span>survey: {f.survey}</span>}
        </div>
      </div>
      {picks.length > 0 && (
        <div className="admin-call-meta" style={{ marginTop: 6 }}>
          {picks.join(', ')}
        </div>
      )}
      {f.comment && <div className="admin-prompt">{f.comment}</div>}
    </div>
  );
}

function AdminUserDetail() {
  const { uid = '' } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<UserDetail | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await adminApi.userDetail(uid);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  if (loading) return <div className="admin-container">Loading user…</div>;

  const profile = data?.profile;
  const auth = data?.authInfo;
  const email = auth?.email || profile?.email || uid;

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <button className="admin-back" onClick={() => navigate('/admin')}>
            ← Back to dashboard
          </button>
          <h1 style={{ marginTop: 6 }}>{email}</h1>
        </div>
        {(auth?.email || profile?.email) && (
          <button
            className="admin-inline-btn"
            onClick={() =>
              navigate(
                `/admin?${new URLSearchParams({
                  tab: 'emails',
                  section: 'compose',
                  to: auth?.email || profile?.email,
                })}`
              )
            }
          >
            Email this user
          </button>
        )}
      </div>

      {error && <div className="admin-error">{error}</div>}

      {data && (
        <>
          <div className="admin-section">
            <h2>Profile</h2>
            <dl className="admin-kv">
              <dt>UID</dt>
              <dd>{uid}</dd>
              <dt>Plan</dt>
              <dd>{profile?.subscriptionPlan || '—'}</dd>
              <dt>Credits remaining</dt>
              <dd>{profile?.creditsRemaining ?? '—'}</dd>
              <dt>Signed up</dt>
              <dd>{fmtAdminDateTime(profile?.createdAt || auth?.createdAt)}</dd>
              <dt>Last sign in</dt>
              <dd>{fmtAdminDateTime(auth?.lastSignInAt)}</dd>
              <dt>Providers</dt>
              <dd>{auth?.providers?.join(', ') || '—'}</dd>
              <dt>Email verified</dt>
              <dd>{auth ? String(auth.emailVerified) : '—'}</dd>
              <dt>Billing</dt>
              <dd>{profile?.billingInterval || '—'}</dd>
              <dt>Stripe customer</dt>
              <dd>{profile?.stripeCustomerId || '—'}</dd>
              {profile?.cancelAt && (
                <>
                  <dt>Cancels at</dt>
                  <dd>
                    {fmtAdminDateTime(profile.cancelAt)} (requested {fmtAdminDateTime(profile.cancelledAt)})
                  </dd>
                </>
              )}
              {profile?.subscriptionEndedAt && (
                <>
                  <dt>Subscription ended</dt>
                  <dd>{fmtAdminDateTime(profile.subscriptionEndedAt)}</dd>
                </>
              )}
            </dl>
          </div>

          {profile?.attribution && (
            <div className="admin-section">
              <h2>First-touch attribution</h2>
              <dl className="admin-kv">
                {Object.entries(profile.attribution).map(([k, v]) => (
                  <div key={k} style={{ display: 'contents' }}>
                    <dt>{k}</dt>
                    <dd>{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="admin-section">
            <h2>Payments ({data.payments.length})</h2>
            {data.payments.length === 0 ? (
              <div className="admin-muted">No payments</div>
            ) : (
              data.payments.map((p, i) => <PaymentRow key={i} p={p} />)
            )}
          </div>

          <div className="admin-section">
            <h2>Subscription events ({data.subscriptionEvents?.length ?? 0})</h2>
            {!data.subscriptionEvents?.length ? (
              <div className="admin-muted">No cancellations, plan changes or failed payments recorded</div>
            ) : (
              data.subscriptionEvents.map((e, i) => <SubscriptionEventRow key={i} e={e} />)
            )}
          </div>

          <div className="admin-section">
            <h2>Emails ({data.emails?.length ?? 0})</h2>
            {!data.emails?.length ? (
              <div className="admin-muted">No emails sent</div>
            ) : (
              data.emails.map((e, i) => <EmailRow key={i} e={e} />)
            )}
          </div>

          <div className="admin-section">
            <h2>Survey feedback ({data.feedback?.length ?? 0})</h2>
            {!data.feedback?.length ? (
              <div className="admin-muted">No survey answers</div>
            ) : (
              data.feedback.map((f, i) => <FeedbackRow key={i} f={f} />)
            )}
          </div>

          <div className="admin-section">
            <h2>Calls ({data.calls.length})</h2>
            {data.calls.length === 0 ? (
              <div className="admin-muted">No calls</div>
            ) : (
              data.calls.map((c) => <CallCard key={c.callId} call={c} />)
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AdminUserDetail;
